const candidatesCardsRecommendationsPerPositionCacheKey = 'getRecommendationsForPosition'

const positionsRecommendationsListCacheKey = 'getRecommendationsPositionList'

export const buildPositionCandidatesRecommendationsCacheKey = ({ positionId }) => [
  candidatesCardsRecommendationsPerPositionCacheKey,
  positionId,
]

export const buildPositionsRecommendationsListCacheKey = ({ hiringTeamId }) => [positionsRecommendationsListCacheKey, hiringTeamId]

const getUpdatedReviewedStatus = (currentTotalCount, currentStatus, updatedTotalCount) => {
  if (currentTotalCount === 0) {
    return currentStatus
  }

  return updatedTotalCount === 0 ? 'completed' : currentStatus
}

const removeCandidateFromPositionRecommendations = (candidateProfileId, recommendationCandidateCards) => {
  return recommendationCandidateCards.nodes.filter(candidate => candidate.id !== candidateProfileId)
}

const getUpdatedTotalCount = currentTotalCount => {
  const updatedTotalCount = currentTotalCount - 1
  return updatedTotalCount
}

const getUpdatedCandidateCardsPages = (candidateProfileId, prevPages) => {
  const updatedPages = prevPages.map(page => {
    const currentTotalCount = page.hiringTeam.position.recommendationCandidateCards.totalCount
    const updatedTotalCount = getUpdatedTotalCount(currentTotalCount)

    const currentReviewStatus = page.hiringTeam.position.recommendationCandidateCards.reviewStatus
    const updatedReviewStatus = getUpdatedReviewedStatus(currentTotalCount, currentReviewStatus, updatedTotalCount)

    const updatedNodes = removeCandidateFromPositionRecommendations(
      candidateProfileId,
      page.hiringTeam.position.recommendationCandidateCards,
    )

    return {
      ...page,
      hiringTeam: {
        ...page.hiringTeam,
        position: {
          ...page.hiringTeam.position,
          recommendationCandidateCards: {
            ...page.hiringTeam.position.recommendationCandidateCards,
            totalCount: updatedTotalCount,
            nodes: updatedNodes,
            reviewStatus: updatedReviewStatus,
          },
        },
      },
    }
  })

  return updatedPages
}

const updatePositionsListCount = (positionId, positionList) => {
  const updatedPositionListCounts = positionList.map(position => {
    if (position.id === positionId) {
      const currentTotalCount = position.recommendationCandidateCards.totalCount
      const updatedTotalCount = getUpdatedTotalCount(currentTotalCount)

      return {
        ...position,
        recommendationCandidateCards: {
          totalCount: updatedTotalCount,
        },
      }
    }
    return position
  })

  return updatedPositionListCounts
}

export const updateRecommendationsList = ({ queryClient, positionId, hiringTeamId, candidateProfileId }) => {
  // update cache for recommendation candidate cards of the position
  const positionRecommendationCandidatesCard = buildPositionCandidatesRecommendationsCacheKey({ positionId })
  const positionRecommendationsCachedData = queryClient.getQueryData(positionRecommendationCandidatesCard)

  if (positionRecommendationsCachedData) {
    queryClient.setQueryData(positionRecommendationCandidatesCard, prevData => {
      if (!prevData) {
        return
      }

      const updatedPages = getUpdatedCandidateCardsPages(candidateProfileId, prevData.pages)

      const updatedQueryData = {
        pageParams: prevData.pageParams,
        pages: updatedPages,
      }

      return updatedQueryData
    })
  }

  // update cache for total count of recommendations for all positions
  const hiringTeamRecommendationCandidatesCard = buildPositionsRecommendationsListCacheKey({ hiringTeamId })
  const recommendationsCountAllPositionCachedData = queryClient.getQueryData(hiringTeamRecommendationCandidatesCard)

  if (recommendationsCountAllPositionCachedData) {
    queryClient.setQueryData(hiringTeamRecommendationCandidatesCard, prevData => {
      if (!prevData) {
        return
      }

      const updatedPositionListCounts = updatePositionsListCount(positionId, prevData.hiringTeam.positions.nodes)

      const updatedQueryData = {
        hiringTeam: {
          positions: {
            nodes: updatedPositionListCounts,
          },
        },
      }

      return updatedQueryData
    })
  }
}

export const updateV2RecommendationsList = ({ queryClient, positionId, candidateProfileId }) => {
  queryClient.setQueriesData(['v2Recommendations', positionId], prevData => {
    if (!prevData) return prevData

    const filteredPages = prevData.pages.map(page => {
      const prevItems = page.data ?? []
      const nextItems = prevItems.filter(candidate => candidate.id !== candidateProfileId)
      return { page, nextItems, removed: prevItems.length - nextItems.length }
    })

    const totalRemoved = filteredPages.reduce((acc, { removed }) => acc + removed, 0)

    const updatedPages = filteredPages.map(({ page, nextItems }) => ({
      ...page,
      data: nextItems,
      total_count: Math.max(0, (page.total_count ?? 0) - totalRemoved),
    }))

    const allRemoved = totalRemoved > 0 && updatedPages.every(page => (page.total_count ?? 0) === 0)
    if (allRemoved) {
      updatedPages[0] = { ...updatedPages[0], review_status: 'completed' }
    }

    return { ...prevData, pages: updatedPages }
  })
}

const addViewedToCandidate = (candidate, candidateProfileId) => {
  if (candidate.id === candidateProfileId) {
    return {
      ...candidate,
      viewed: true,
    }
  }

  return candidate
}

export const addViewedToRecommendationCardsCache = ({ queryClient, positionId, candidateProfileId }) => {
  const positionRecommendationCandidatesCard = buildPositionCandidatesRecommendationsCacheKey({ positionId })
  const positionRecommendationsCachedData = queryClient.getQueryData(positionRecommendationCandidatesCard)

  if (positionRecommendationsCachedData) {
    queryClient.setQueryData(positionRecommendationCandidatesCard, prevData => {
      if (!prevData) {
        return
      }

      const updatedPages = prevData.pages.map(page => ({
        ...page,
        hiringTeam: {
          ...page.hiringTeam,
          position: {
            ...page.hiringTeam.position,
            recommendationCandidateCards: {
              ...page.hiringTeam.position.recommendationCandidateCards,
              nodes: page.hiringTeam.position.recommendationCandidateCards.nodes.map(candidate =>
                addViewedToCandidate(candidate, candidateProfileId),
              ),
            },
          },
        },
      }))

      const updatedQueryData = {
        pageParams: prevData.pageParams,
        pages: updatedPages,
      }

      return updatedQueryData
    })
  }
}
